import { DetailField, GridTableEmptyList, PageBreadcrumb } from "@/modules/common/components";
import { formatDate } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import { useParams } from "react-router-dom";
import useLogDetails from "../../hooks/useLogDetails";
import LogChangeCard from "./components/LogChangeCard";
import LogDetailsSkeleton from "./components/LogDetailsSkeleton";
import LogStatusBadge from "./components/LogStatusBadge";
import { MANAGEMENT_PERFORMER_TYPE_OPTIONS } from "@/modules/common/types/ManagementPerformerType.enum";

const LogsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { data: log, isLoading } = useLogDetails(id);

  if (isLoading || log === undefined) {
    return <LogDetailsSkeleton />;
  }

  const performerTypeLabel = MANAGEMENT_PERFORMER_TYPE_OPTIONS.find((type) => type.value === log.performedByType)?.label;

  return (
    <div className="flex flex-col gap-4">
      <PageBreadcrumb items={[{ label: "Logs / Auditoria", to: ROUTES.LOGS.LIST }, { label: "Detalhes do log" }]} />

      <div className="flex min-w-0 justify-between items-center">
        <div className="flex min-w-0 flex-col">
          <p className="text-[22px] font-bold text-ui-primary wrap-break-word">{log.operation ?? "Detalhes do log"}</p>
          <p className="text-[14px] text-ui-text-muted wrap-break-word">
            Registro completo do evento de auditoria em {formatDate(log.createdAt, "DD/MM/YYYY [às] HH:mm")}.
          </p>
        </div>
      </div>

      <div className="flex flex-col bg-white rounded-xl gap-4 p-4">
        <div className="flex flex-col">
          <p className="text-[16px] font-bold text-ui-primary">Dados Gerais</p>
          <p className="text-[12px] text-ui-text-muted">Informações da requisição e do responsável pela ação.</p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DetailField label="Operação">{log.operation}</DetailField>

          <DetailField label="Status">
            <LogStatusBadge success={log.success} />
          </DetailField>

          <DetailField label="Responsável">{log.performer?.name}</DetailField>

          <DetailField label="Tipo de usuário">{performerTypeLabel ?? "-"}</DetailField>

          <DetailField label="Data e Hora">{formatDate(log.createdAt, "DD/MM/YYYY [às] HH:mm:ss")}</DetailField>

          <DetailField label="Domínio">{log.domain}</DetailField>

          <DetailField label="Método">{log.method}</DetailField>

          <DetailField label="IP">{log.ip}</DetailField>

          <DetailField label="Caminho" className="md:col-span-2" valueClassName="font-mono">
            {log.path}
          </DetailField>

          {log.query && (
            <DetailField label="Query" className="md:col-span-2" valueClassName="font-mono">
              {log.query}
            </DetailField>
          )}

          <DetailField label="User-Agent" className="md:col-span-2" valueClassName="font-mono">
            {log.userAgent}
          </DetailField>

          {log.error && (
            <DetailField label="Erro" className="md:col-span-2" valueClassName="bg-red-50 border-red-100 text-red-700">
              {log.error}
            </DetailField>
          )}
        </div>
      </div>

      <div className="flex flex-col bg-white rounded-xl gap-4 p-4">
        <div className="flex flex-col">
          <p className="text-[16px] font-bold text-ui-primary">Alterações</p>
          <p className="text-[12px] text-ui-text-muted">Registros de dados alterados por esta ação.</p>
        </div>

        {!log.changes || log.changes.length === 0 ? (
          <GridTableEmptyList />
        ) : (
          <div className="flex flex-col gap-3">
            {log.changes.map((change) => (
              <LogChangeCard key={change.id} change={change} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsDetails;
